import type React from "react";

import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Stack, Typography } from "@mui/material";
import clsx from "clsx";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import type { UserCourseLesson } from "api/schemas/UserCourse";
import type { DurationWeeks, PatientTreatmentScheduleExercise } from "api/services/treatment/schemas/Treatment";
import {
  ActivityImage,
  DragHandle,
  ExerciseControls,
} from "routes/patients/views/Treatment/components/TreatmentEditor/shared";
import { BaseButton } from "shared/atoms/BaseButton";
import { Tooltip } from "shared/atoms/Tooltip";
import { DARK_BLUE } from "theme/colors";

import type { HandleLevelChange, HandleSetsAndRepsChange, HandleWeekRangeChange, TimelineActivity } from "../types";

export const isExercise = (
  activity: PatientTreatmentScheduleExercise | TimelineActivity | UserCourseLesson
): activity is PatientTreatmentScheduleExercise => "exercise_id" in activity;

const normalizeActivity = (activity: PatientTreatmentScheduleExercise | TimelineActivity | UserCourseLesson) => {
  const getLevels = () => {
    if (isExercise(activity)) {
      return activity.max_level;
    }
    if ("parts_count" in activity) {
      return activity.parts_count;
    }
    return 0;
  };

  const videoThumbnail = "video_thumbnail_file_url" in activity ? activity.video_thumbnail_file_url : null;
  const variationTitle =
    "variation_title" in activity && activity.variation_title ? ` (${activity.variation_title})` : "";

  return {
    imageUrl: activity.image_url || videoThumbnail,
    levels: getLevels() as number,
    pinned: "pinned" in activity ? activity.pinned : false,
    title: `${activity.title}${variationTitle}`,
    type: isExercise(activity) ? "exercise" : "lesson",
  };
};

type ExerciseProps = {
  durationWeeks?: DurationWeeks;
  exercisesPerDay?: number;
  highestWeekNumber?: number;
  pinnedExercisesCount?: number;
  weekNumber?: number;
  onPin?: () => void;
  onLevelChange?: HandleLevelChange;
  onSetsAndRepsChange?: HandleSetsAndRepsChange;
  onToggleLevelLock?: HandleLevelChange;
  onWeekRangeChange?: HandleWeekRangeChange;
};

interface ActivityItemProps {
  activity: PatientTreatmentScheduleExercise | TimelineActivity | UserCourseLesson;
  exerciseProps?: ExerciseProps;
  onPreview?: () => void;
  onRemove?: () => void;
}

export const ActivityItem: React.FC<ActivityItemProps> = ({ activity, exerciseProps, onPreview, onRemove }) => {
  const { t } = useTranslation();
  const normalizedActivity = normalizeActivity(activity);

  const { attributes, isDragging, listeners, transform, transition, setNodeRef } = useSortable({
    // TODO: Use a more specific ID that includes the activity type to avoid potential conflicts between exercises and lessons
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    id: activity.id,
    data: {
      type: "activity_card",
      activity,
      activityType: normalizedActivity.type,
      weekNumber: exerciseProps?.weekNumber,
    },
  });

  const isDraggable = normalizedActivity.type === "lesson";
  const style = !isDraggable
    ? {}
    : {
        transform: CSS.Transform.toString(transform),
        transition,
      };
  const dragHandleProps = !isDraggable ? {} : { ...attributes, ...listeners };
  const canPinExercise =
    normalizedActivity.pinned || (exerciseProps?.pinnedExercisesCount ?? 0) < (exerciseProps?.exercisesPerDay ?? 3);

  return (
    <Container
      ref={!isDraggable ? null : setNodeRef}
      className={clsx({
        draggable: isDraggable,
        dragging: isDraggable && isDragging,
        locked: "level_locked" in activity && activity.level_locked,
      })}
      style={style}
      {...dragHandleProps}
    >
      <Stack direction="row" spacing={1.5} sx={{ alignItems: "center" }}>
        {isDraggable && <DragHandle isDisabled={false} />}

        <ActivityImage src={normalizedActivity.imageUrl} title={normalizedActivity.title} />

        <Stack spacing={0.5}>
          <Typography variant="body_bold">{normalizedActivity.title}</Typography>
          {normalizedActivity.levels > 0 && (
            <Typography variant="label" color={DARK_BLUE[70]}>
              {t("treatment.editor.activity_levels", {
                activityType: normalizedActivity.type,
                count: normalizedActivity.levels,
              })}
            </Typography>
          )}
        </Stack>

        <Stack
          direction="row"
          spacing={1}
          sx={{ flexShrink: 1, justifyContent: "flex-end", flexWrap: "wrap", marginLeft: "auto" }}
        >
          {onPreview && (
            <BaseButton
              variant="action"
              icon="eye"
              size="small"
              title={t("treatment.editor.preview_activity", { activityType: normalizedActivity.type })}
              onClick={event => {
                event.stopPropagation();
                onPreview();
              }}
            />
          )}

          {exerciseProps?.onPin && (
            <Tooltip
              arrow
              disabled={canPinExercise}
              placement="top"
              title={t("treatment.editor.pin_exercise.tooltip", {
                exercisesPerDay: exerciseProps?.exercisesPerDay,
              })}
            >
              <BaseButton
                className={clsx({ active: "pinned" in activity && activity.pinned })}
                disabled={!canPinExercise}
                variant="action"
                icon={"pinned" in activity && activity.pinned ? "thumbtack-solid" : "thumbtack"}
                size="small"
                title={t("treatment.editor.pin_exercise.title")}
                onClick={event => {
                  event.stopPropagation();
                  exerciseProps?.onPin?.();
                }}
                style={{ flexBasis: "32px" }}
              />
            </Tooltip>
          )}

          {onRemove && (
            <BaseButton
              variant="action"
              className="danger"
              icon="trash"
              size="small"
              title={t("treatment.editor.remove_activity", { activityType: normalizedActivity.type })}
              onClick={event => {
                event.stopPropagation();
                onRemove();
              }}
              style={{ flexBasis: "32px" }}
            />
          )}
        </Stack>
      </Stack>

      {isExercise(activity) && exerciseProps?.durationWeeks && exerciseProps?.highestWeekNumber && (
        <ExerciseControls
          durationWeeks={exerciseProps.durationWeeks}
          highestWeekNumber={exerciseProps.highestWeekNumber}
          exercise={activity}
          onLevelChange={exerciseProps?.onLevelChange}
          onSetsAndRepsChange={exerciseProps?.onSetsAndRepsChange}
          onToggleLevelLock={exerciseProps?.onToggleLevelLock}
          onWeekRangeChange={exerciseProps?.onWeekRangeChange}
        />
      )}
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 12px;
  background-color: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.redesign.db30};
  border-radius: 12px;

  &:hover {
    background-color: ${({ theme }) => theme.colors.redesign.db1};
    border-color: ${({ theme }) => theme.colors.redesign.db40};
  }

  &.draggable {
    cursor: grab;

    &:active {
      cursor: grabbing;
    }
  }

  &.dragging {
    opacity: 0.5;
    transition: none;
  }

  &.locked {
    border-color: ${({ theme }) => theme.colors.redesign.o60};

    &:hover {
      border-color: ${({ theme }) => theme.colors.redesign.o100};
    }
  }
`;
